import React, { useState } from 'react';
import './PaymentMethodSelector.css';

export interface PaymentMethod {
  /** Unique identifier */
  id: string;
  /** Card type (visa, mastercard, amex, etc.) */
  type: string;
  /** Last 4 digits of the card */
  last4: string;
  /** Expiry date (e.g. "12/25") */
  expiry: string;
  /** Whether this is the default payment method */
  isDefault?: boolean;
}

export interface PaymentMethodSelectorProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'onChange' | 'defaultValue'
> {
  /** Available payment methods */
  methods: PaymentMethod[];
  /** Controlled: currently selected method ID */
  value?: string;
  /** Uncontrolled: initially selected method ID */
  defaultValue?: string;
  /** Called when selection changes */
  onChange?: (methodId: string) => void;
  /** Show "Add new payment method" option */
  showAddNew?: boolean;
  /** Called when "Add new" is clicked */
  onAddNew?: () => void;
}

export const PaymentMethodSelector: React.FC<PaymentMethodSelectorProps> = ({
  methods,
  value,
  defaultValue,
  onChange,
  showAddNew = false,
  onAddNew,
  className = '',
  ...rest
}) => {
  const [internalValue, setInternalValue] = useState(defaultValue || methods[0]?.id || '');

  const selectedValue = value !== undefined ? value : internalValue;

  const handleSelect = (methodId: string) => {
    if (value === undefined) {
      setInternalValue(methodId);
    }
    onChange?.(methodId);
  };

  const classes = ['hi-ui-payment-method-selector', className].filter(Boolean).join(' ');

  const formatType = (type: string) => {
    const labels: Record<string, string> = {
      visa: 'Visa',
      mastercard: 'Mastercard',
      amex: 'American Express',
      discover: 'Discover',
    };
    return labels[type.toLowerCase()] || type;
  };

  return (
    <div className={classes} role="radiogroup" aria-label="Payment method" {...rest}>
      {methods.map((method) => (
        <label
          key={method.id}
          className={[
            'hi-ui-payment-method-selector__option',
            selectedValue === method.id ? 'hi-ui-payment-method-selector__option--selected' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <input
            type="radio"
            name="payment-method"
            className="hi-ui-payment-method-selector__radio"
            value={method.id}
            checked={selectedValue === method.id}
            onChange={() => handleSelect(method.id)}
          />
          <span className="hi-ui-payment-method-selector__indicator" />
          <span className="hi-ui-payment-method-selector__details">
            <span className="hi-ui-payment-method-selector__type">{formatType(method.type)}</span>
            <span className="hi-ui-payment-method-selector__number">
              &bull;&bull;&bull;&bull; {method.last4}
            </span>
            <span className="hi-ui-payment-method-selector__expiry">Exp {method.expiry}</span>
          </span>
          {method.isDefault && (
            <span className="hi-ui-payment-method-selector__default">Default</span>
          )}
        </label>
      ))}
      {showAddNew && (
        <button type="button" className="hi-ui-payment-method-selector__add-new" onClick={onAddNew}>
          <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
            <path d="M8 2a.75.75 0 01.75.75v4.5h4.5a.75.75 0 010 1.5h-4.5v4.5a.75.75 0 01-1.5 0v-4.5h-4.5a.75.75 0 010-1.5h4.5v-4.5A.75.75 0 018 2z" />
          </svg>
          Add new payment method
        </button>
      )}
    </div>
  );
};
